/**
 * Core API Types for Regional Sales Analysis SPA
 * Generated from data-model.md
 */

// =============================================================================
// User & Authentication Types
// =============================================================================

/**
 * Represents an authenticated user in the system.
 * Matches backend UserResponseDTO
 */
export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  role: 'Admin' | 'User';
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  userType: 'Internal' | 'External';
  approvalStatus: 'Pending' | 'Approved' | 'Rejected';
  approvedAt: string | null;
  approvedBy: string | null;

  // Profile
  salutation: string | null;
  jobTitle: string | null;
  phoneNumber: string | null;
  organization: string | null;
  department: string | null;
  customerNumber: string | null;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;

  // Email verification
  emailVerified: boolean;
  emailVerifiedAt: string | null;

  // Credits
  credits: number;

  // Marketing consent
  marketingOptIn?: boolean;

  // Tracking opt-out (Art. 21 DSGVO)
  trackingOptOut?: boolean;

  // QR code image URLs (relative paths)
  linkedInQrCodeUrl: string | null;
  companyQrCodeUrl: string | null;
}

/**
 * Represents the current authentication state.
 */
export interface AuthSession {
  /** Current user or null if unauthenticated */
  user: User | null;
  /** JWT or session token */
  accessToken: string | null;
  /** Token expiration (ISO 8601) */
  expiresAt: string | null;
  /** Computed from user presence */
  isAuthenticated: boolean;
}

// =============================================================================
// Analysis Types
// =============================================================================

/**
 * Analysis processing status
 */
export type AnalysisStatus =
  | 'pending'      // Submitted, not yet processing
  | 'processing'   // Currently being processed
  | 'completed'    // Successfully finished
  | 'failed';      // Processing failed

/**
 * Represents a sales analysis instance.
 */
export interface Analysis {
  /** Unique identifier */
  id: string;
  /** Owner's user ID */
  userId: string;
  /** Analysis display title */
  title: string;
  /** Current processing status */
  status: AnalysisStatus;
  /** ISO 8601 timestamp */
  createdAt: string;
  /** ISO 8601 timestamp (when done) */
  completedAt?: string;
  /** Form data submitted */
  inputParameters: AnalysisInput;
  /** Result components */
  resultCards: AnalysisResultCard[];
  /** Whether premium features unlocked */
  isPremium: boolean;
}

/**
 * Form data collected during analysis creation.
 */
export interface AnalysisInput {
  /** Step data organized by step number */
  steps: Record<number, StepData>;
}

/**
 * Data for a single form step
 */
export interface StepData {
  fields: Record<string, FieldValue>;
}

/**
 * Supported field value types (text and number only per requirements)
 */
export type FieldValue = string | number | null;

// =============================================================================
// Result Card Types
// =============================================================================

/**
 * Result card content type
 */
export type ResultCardType =
  | 'chart'
  | 'image'
  | 'text'
  | 'table';

/**
 * Card loading status
 */
export type CardStatus =
  | 'loading'     // Currently being processed
  | 'ready'       // Data available
  | 'error';      // Processing failed

/**
 * Individual result component within an analysis.
 */
export interface AnalysisResultCard {
  /** Unique card identifier */
  id: string;
  /** Content type */
  type: ResultCardType;
  /** Card header */
  title: string;
  /** 0-100 percentage */
  loadingProgress: number;
  /** Current card status */
  status: CardStatus;
  /** Brief description */
  summary: string;
  /** Content when loaded */
  data: ResultCardData | null;
  /** Additional detail content */
  expandedData?: ExpandedCardData;
}

/**
 * Type-specific result card content
 */
export interface ResultCardData {
  chartConfig?: ChartConfig;
  imageUrl?: string;
  textContent?: string;
  tableData?: TableData;
}

/**
 * Additional content for expanded card view
 */
export interface ExpandedCardData {
  additionalCharts?: ChartConfig[];
  detailText?: string;
  footnotes?: string[];
}

// =============================================================================
// Chart Types
// =============================================================================

/**
 * Supported chart types
 */
export type ChartType = 'line' | 'bar' | 'pie' | 'area' | 'donut' | 'radar' | 'scatter' | 'heatmap';

/**
 * Chart data series
 */
export interface ChartSeries {
  name: string;
  data: (number | null)[];
}

/**
 * ApexCharts options type (subset used by the app)
 */
export interface ApexChartOptions {
  chart?: {
    type?: ChartType;
    height?: number | string;
    width?: number | string;
    toolbar?: { show?: boolean };
  };
  xaxis?: {
    categories?: string[];
    labels?: { show?: boolean };
  };
  yaxis?: {
    labels?: { show?: boolean };
  };
  colors?: string[];
  title?: {
    text?: string;
    align?: 'left' | 'center' | 'right';
  };
  legend?: {
    show?: boolean;
    position?: 'top' | 'right' | 'bottom' | 'left';
  };
  dataLabels?: {
    enabled?: boolean;
  };
  stroke?: {
    curve?: 'smooth' | 'straight' | 'stepline';
    width?: number;
  };
  fill?: {
    type?: 'solid' | 'gradient' | 'pattern';
  };
}

/**
 * Configuration for chart visualization (ApexCharts format).
 */
export interface ChartConfig {
  type: ChartType;
  series: ChartSeries[];
  options: ApexChartOptions;
}

// =============================================================================
// Table Types
// =============================================================================

/**
 * Single table row
 */
export interface TableRow {
  cells: (string | number)[];
}

/**
 * Tabular data for result cards.
 */
export interface TableData {
  headers: string[];
  rows: TableRow[];
}

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Response from login endpoint
 * Matches backend AuthResponseDTO
 */
export interface LoginResponse {
  /** JWT access token */
  accessToken: string;
  /** Token type (e.g., 'Bearer') */
  tokenType: string;
  /** Token expiration time in seconds */
  expiresIn: number;
  /** Authenticated user data */
  user: User;
}

/**
 * Response from analyses list endpoint
 */
export interface AnalysisListResponse {
  analyses: Analysis[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Response from analysis progress endpoint
 */
export interface AnalysisProgressResponse {
  analysisId: string;
  status: AnalysisStatus;
  cards: {
    cardId: string;
    loadingProgress: number;
    status: CardStatus;
  }[];
}

/**
 * Response from premium upgrade endpoint
 */
export interface PremiumUpgradeResponse {
  analysisId: string;
  paymentUrl: string;
}

// =============================================================================
// Request Types
// =============================================================================

/**
 * Request payload for email/password login
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Request payload for requesting access (public registration)
 */
export interface RequestAccessRequest {
  firstName: string;
  lastName: string;
  organization: string;
  jobTitle: string;
  phoneNumber: string;
  department?: string;
  email: string;
  marketingOptIn?: boolean;
}

/**
 * Response from request access endpoint
 */
export interface RequestAccessResponse {
  success: boolean;
  message: string;
}

/**
 * Request payload for magic link verification
 */
export interface MagicLinkVerifyRequest {
  token: string;
}

/**
 * Request payload for requesting password reset
 */
export interface RequestPasswordResetRequest {
  email: string;
}

/**
 * Response from password reset request endpoint
 */
export interface RequestPasswordResetResponse {
  message: string;
}

/**
 * Request payload for resetting password with token
 */
export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

/**
 * Response from reset password endpoint
 */
export interface ResetPasswordResponse {
  message: string;
}

/**
 * Request payload for creating a new analysis
 */
export interface CreateAnalysisRequest {
  title: string;
  inputParameters: AnalysisInput;
}

// =============================================================================
// Processing Request Types (Dashboard Display)
// =============================================================================

/**
 * Processing status enum values
 */
export enum ProcessingStatus {
  Pending = 0,
  InProgress = 1,
  Completed = 2,
  Failed = 3,
}

/**
 * Payload parameter from a processing request
 */
export interface PayloadParameter {
  parameterName: string;
  payload: string;
}

/**
 * Processing request as returned by the API
 */
export interface ProcessingRequest {
  id: number;
  productId: number;
  productName: string;
  status: ProcessingStatus;
  createdAt: string;
  finishedAt?: string;
  executionStartedAt?: string;
  executionFinishedAt?: string;
  correlationId: string;
  attempts: number;
  lastErrorMessage?: string | null;
  updatedAt: string;
  userId: string;
  userEmail: string;
  userName: string;
  payloadParametersCount: number;
  featuresCount: number;
  payloadParameters: PayloadParameter[];
}

/**
 * Response from processing requests list endpoint
 */
export interface ProcessingRequestListResponse {
  requests: ProcessingRequest[];
  total: number;
  page: number;
  pageSize: number;
}

